import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { ClockIcon } from "@phosphor-icons/react/dist/csr/Clock";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { Formik, Form } from "formik";
import { useState } from "react";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { SERVICE_TITAN_STATUSES } from "@/business/constants";
import {
  useServiceTitanIntegration,
  ServiceTitanFormValues,
} from "@/business-settings/business/business-service-titan-settings/useServiceTitanIntegration";
import { formatValidationErrors } from "@/core/api/errors";
import { ApiError } from "@/core/api/types";
import { TimedButton } from "./timed-button";

const STATUS_MESSAGES = {
  SUCCESS: "ServiceTitan integration updated successfully",
  PROCESSING: "Saved. We're processing your information.",
  CONNECTION_FAILED: "Connection failed. Please check your credentials.",
  UPDATE_FAILED: "Failed to update ServiceTitan integration. Please try again.",
} as const;

interface ServiceTitanIntegrationModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  businessId?: string;
}

export const ServiceTitanIntegrationModal = ({
  isOpen,
  onOpenChange,
  businessId,
}: ServiceTitanIntegrationModalProps) => {
  const [lastSubmittedValues, setLastSubmittedValues] =
    useState<ServiceTitanFormValues | null>(null);

  const {
    status,
    config,
    initialValues,
    validationSchema,
    setHasConnectionError,
    isLoading,
    isPending,
    handleSubmit: handleSubmitCredentials,
    showTenantIdField,
    showClientFields,
    showErrorState,
    showSuccessState,
  } = useServiceTitanIntegration({
    businessId,
    showClientAndSecretKey: "modalShowClientAndSecret",
    hideTenantIdWhenEstablished: true,
    onSuccess: (resp) => {
      const nextStatus = resp.data?.credentials_status;
      const isComplete =
        nextStatus === SERVICE_TITAN_STATUSES.connection_established;
      const connectionFailed =
        nextStatus === SERVICE_TITAN_STATUSES.credentials_received;

      if (isComplete) {
        toast.success(STATUS_MESSAGES.SUCCESS);
        setHasConnectionError(false);
      } else if (connectionFailed) {
        setHasConnectionError(true);
        toast.error(STATUS_MESSAGES.CONNECTION_FAILED);
      } else {
        toast.success(STATUS_MESSAGES.PROCESSING);
        setHasConnectionError(false);
      }
    },
    onError: (error: Error) => {
      let description: string | undefined;

      if (error instanceof ApiError && error.details) {
        description = formatValidationErrors(error.details);
      }

      toast.error(STATUS_MESSAGES.UPDATE_FAILED, {
        description: description || "Please try again.",
      });
      setHasConnectionError(false);
    },
  });

  const handleClose = () => {
    if (!isPending) {
      setHasConnectionError(false);
      onOpenChange(false);
    }
  };

  const isFormDisabled = (values: ServiceTitanFormValues) =>
    !businessId ||
    isPending ||
    (lastSubmittedValues &&
      JSON.stringify(values) === JSON.stringify(lastSubmittedValues));

  const handleSubmit = (values: ServiceTitanFormValues) => {
    setLastSubmittedValues(values);
    handleSubmitCredentials(values);
  };

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Loading ServiceTitan integration...</DialogTitle>
          </DialogHeader>
          <Stack gap={6} align="center" className="w-full pb-4">
            <Spinner aria-hidden size={24} className="text-subtle" />
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>ServiceTitan integration</DialogTitle>
        </DialogHeader>

        <Formik<ServiceTitanFormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnMount={true}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, isValid, getFieldProps }) => (
            <Form>
              <Stack align="start" gap={4} className="w-full pb-4">
                {showErrorState ? (
                  <Alert color="danger">
                    <WarningIcon aria-hidden />
                    <AlertDescription>
                      We were unable to connect to your ServiceTitan, please
                      check that all values are correct and submit again.
                    </AlertDescription>
                  </Alert>
                ) : (
                  !!config.modalSubtitle && (
                    <Stack
                      className="w-full"
                      gap={4}
                      align="center"
                      justify="center"
                      direction="row"
                    >
                      {status !== SERVICE_TITAN_STATUSES.awaiting_tenant_id &&
                        !showSuccessState && (
                          <ClockIcon className="h-6 w-6 text-success" />
                        )}
                      <Text className="text-center text-subtle">
                        {config.modalSubtitle}
                      </Text>
                    </Stack>
                  )
                )}

                <Stack className="w-full" align="start" gap={4}>
                  {showTenantIdField && (
                    <FormInput
                      label="Tenant ID"
                      placeholder="Enter your ServiceTitan tenant ID"
                      {...getFieldProps("tenant_id")}
                      fullWidth
                      disabled={isPending}
                      required
                    />
                  )}

                  {showClientFields && (
                    <>
                      <FormInput
                        label="Client ID"
                        placeholder="Enter your ServiceTitan client ID"
                        {...getFieldProps("client_id")}
                        fullWidth
                        disabled={isPending}
                        required
                      />
                      <FormInput
                        label="Client Secret"
                        placeholder="Enter your ServiceTitan client secret"
                        type="password"
                        autoSave="off"
                        {...getFieldProps("client_secret")}
                        fullWidth
                        disabled={isPending}
                        required
                      />
                    </>
                  )}
                </Stack>

                {showSuccessState && (
                  <Stack className="w-full" gap={4} align="center">
                    <CheckCircleIcon className="h-10 w-10 text-success" />
                  </Stack>
                )}

                {config.description && (
                  <Stack className="w-full" gap={2} align="center">
                    <Text className="text-center text-subtle">
                      {config.description}
                    </Text>
                  </Stack>
                )}
              </Stack>

              <DialogFooter>
                {config.modalShowSubmitButton ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleClose}
                      disabled={isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="min-w-[84px]"
                      disabled={
                        !isValid || isFormDisabled(values || false) || isPending
                      }
                      loading={isPending}
                      loadingLabel="Save, in progress"
                    >
                      Save
                    </Button>
                  </>
                ) : (
                  <Stack className="w-full" gap={4} align="center">
                    <TimedButton
                      type="button"
                      onTimeout={handleClose}
                      onClick={handleClose}
                    >
                      Close
                    </TimedButton>
                  </Stack>
                )}
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
